import React from "react";
import { useNavigate } from "react-router-dom";
import { Factory, Layers, ArrowRight, LogOut } from "lucide-react";
import { useBusinessUnit, BUSINESS_UNITS } from "@/context/BusinessUnitContext";
import { useAuth } from "@/context/AuthContext";

/**
 * Shown after login when the user hasn't picked a vertical yet, and also
 * reachable via the header switcher to change vertical anytime.
 */
export default function SelectVertical() {
  const { setBusinessUnit } = useBusinessUnit();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const iconFor = (key) => (key === "bopp_tape" ? Layers : Factory);

  const pick = (key) => {
    setBusinessUnit(key);
    navigate("/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col" data-testid="select-vertical-page">
      <header className="border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 bg-orange-600 flex items-center justify-center text-white font-mono font-bold text-xs tracking-tight">WP</div>
          <div>
            <div className="text-white font-semibold tracking-tight text-sm">Wellpack Polyplast</div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">Choose your workspace</div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-[10px] font-mono uppercase text-zinc-500">
          <span>{user?.email}</span>
          <button
            data-testid="select-vertical-logout"
            onClick={logout}
            className="inline-flex items-center gap-1 px-2 py-1 border border-zinc-700 hover:bg-zinc-900 hover:text-white"
          >
            <LogOut size={11} /> Sign out
          </button>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="max-w-4xl w-full">
          <div className="text-center mb-10">
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white">Choose a Business Unit</h1>
            <p className="text-sm text-zinc-400 mt-2 max-w-lg mx-auto">
              Each vertical has its own inventory, orders, and production data. Customers, suppliers, and users are shared.
              You can switch between them anytime from the header.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {BUSINESS_UNITS.map((bu) => {
              const Icon = iconFor(bu.key);
              return (
                <button
                  key={bu.key}
                  data-testid={`select-vertical-${bu.key}`}
                  onClick={() => pick(bu.key)}
                  className="group relative bg-zinc-900 border-2 border-zinc-800 hover:border-white transition-colors p-8 text-left overflow-hidden"
                  style={{ borderTopColor: bu.accent, borderTopWidth: "6px" }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="h-14 w-14 flex items-center justify-center shrink-0"
                      style={{ background: bu.accent }}
                    >
                      <Icon size={26} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">{bu.short}</div>
                      <div className="text-xl font-semibold text-white leading-tight">{bu.label}</div>
                      <div className="text-xs font-mono text-zinc-400 mt-2">{bu.tagline}</div>
                    </div>
                  </div>
                  <div className="mt-6 flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-zinc-500 group-hover:text-white transition-colors">
                    <span>Enter workspace</span>
                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-8 text-center text-[10px] font-mono uppercase tracking-widest text-zinc-600">
            Data isolation · shared customers &amp; suppliers · single sign-on
          </div>
        </div>
      </main>
    </div>
  );
}
